/**
 * B080090 送り状向けファイル出力
 */

import { Column, Query, QueryResult } from "@material-table/core";
import {
  B000150TokuiNm1,
  B000223BatchNm,
  B080090Find,
  B080090GetExcel,
  B080090Request,
  catchFileError,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import { getSCode, SCode, Torikomisaki } from "messages/codes";
import { labels } from "messages/labels";
import { B000150 } from "pages/b000/B000150";
import { B000223 } from "pages/b000/B000223";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, download, mapIds } from "services";
import { focusState, useFocus } from "states";

// プロパティ
type Props = {};

// コンポーネントデータ定義
type Fields = Partial<{
  torikomisaki: SCode;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNm: string;
  shukashijiYmd: string;
  shijikaisu: string;
  batchNo: string;
  batchNm: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  shukashijiYmd: string;
  shijikaisu: string;
  batchNo: string;
  batchNm: string;
  shukayoteiYmd: string;
  nohinyoteiYmd: string;
  juchudenpyoNo: string;
  tokuiNmView: string;
  nonyuNmView: string;
  haisobin: string;
  hasokbnNm: string;
}>;

const focusId = "B080090";

// B080090コンポーネント
export const B080090 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

// 画面定義
const Page = (props: Props) => {
  // コンポーネント設定

  // フォーム設定
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: {
      torikomisaki: getSCode(Torikomisaki, "1"),
      tokuiCd: "",
      tokuishitenCd: "",
      tokuiNm: "",
      shukashijiYmd: date(),
      shijikaisu: "",
      batchNo: "",
      batchNm: "",
    },
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // メッセージ
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // エクセル出力ボタンの非活性制御
  const [disabledExcelBtn, setDisabledExcelBtn] = useState(true);

  // テーブル設定

  // テーブル参照
  const tableRef = createRef();
  // ページサイズ
  const pageSize = 100;
  // 初回検索フラグ
  const searched = useRef(false);
  // 初回ページフラグ
  const isDefaultPage = useRef(false);
  // カラム定義
  const columns: Column<Data>[] = [
    { field: "id", hidden: true },
    { field: "shukashijiYmd", title: "出荷指示日", width: "5.5rem" },
    { field: "shijikaisu", title: "指示回数", width: "3rem", align: "right" },
    { field: "batchNm", title: "バッチ", width: "12.5rem" },
    { field: "shukayoteiYmd", title: "出荷日", width: "5.5rem" },
    { field: "nohinyoteiYmd", title: "必着日", width: "5.5rem" },
    { field: "juchudenpyoNo", title: "受注No.", width: "7rem" },
    { field: "tokuiNmView", title: "得意先", width: "24rem" },
    { field: "nonyuNmView", title: "納入先", width: "24rem" },
    { field: "haisobin", title: "配送便", width: "8rem" },
    { field: "hasokbnNm", title: "発送区分", width: "5rem" },
  ];

  //イベント

  // 初期表示
  useEffect(() => focus(1));

  // 得意先表示
  const handleBlurTokuiNm = () => B000150TokuiNm1(methods, navigate, setMsg);

  // 得意先照会から設定
  const handleSubmitTokuiNm = (data: any) => {
    methods.setValue("tokuiCd", data.tokuiCd);
    methods.setValue("tokuishitenCd", data.tokuishitenCd);
    methods.setValue("tokuiNm", data.tokuiNm1);
  };

  // バッチ名表示
  const handleBlurBatchNm = () => B000223BatchNm(methods, navigate, setMsg);

  // バッチ番号照会から設定
  const handleSubmitBatchNm = (data: any) => {
    methods.setValue("batchNo", data.batchNo);
    methods.setValue("batchNm", data.batchNm);
  };

  // 検索初期化
  const handleResetSearch = () => {
    // Excel出力ボタン非活性
    setDisabledExcelBtn(true);
    // データクリア
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリック
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索済みに変更
    searched.current = true;
    // 初期ページに変更
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索処理
  const handleSearch = (query: Query<Data>) => {
    return new Promise<QueryResult<Data>>((resolve) => {
      // 検索ボタンクリック以外で実行された場合、無効とする。
      if (!searched.current) {
        resolve({ data: [], page: 0, totalCount: 0 });
        return;
      }

      // 初期ページの場合はページ数を0にする。
      query.page = isDefaultPage.current ? 0 : query.page;

      // フォーム取得
      const fields = methods.getValues();

      // リクエスト作成
      const param: B080090Request = {
        torikomisaki: fields.torikomisaki?.value,
        tokuiCd: fields.tokuiCd,
        tokuishitenCd: fields.tokuishitenCd,
        shukashijiYmd: fields.shukashijiYmd,
        shijikaisu: fields.shijikaisu,
        batchNo: fields.batchNo,
        page: query.page,
        size: query.pageSize,
      };

      // API呼び出し
      B080090Find(param)
        .then((value) => {
          // 対象データが存在しない場合エラーを表示する。
          if (value.data.totalElements === 0) {
            setMsg(getClientError("B080090_001"));
          } else {
            // Excel出力ボタン活性
            setDisabledExcelBtn(false);
          }
          // テーブルに設定する。
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "id", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // エクセル出力
  const handleClickExcelButton = () => {
    // フォーム取得
    const fields = methods.getValues();

    // リクエスト作成
    const param: B080090Request = {
      torikomisaki: fields.torikomisaki?.value,
      tokuiCd: fields.tokuiCd,
      tokuishitenCd: fields.tokuishitenCd,
      shukashijiYmd: fields.shukashijiYmd,
      shijikaisu: fields.shijikaisu,
      batchNo: fields.batchNo,
    };

    // API呼び出し
    B080090GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B080090_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト
  return (
    <FormProvider {...methods}>
      <SFrame
        title="送り状向けファイル出力"
        gridTemplateColumns={"6rem 9rem 6rem 4rem 6rem 6rem 5rem 20rem"}
      >
        <SLabel required>取込先</SLabel>
        <SCodeSelect
          label="取込先"
          name="torikomisaki"
          options={Torikomisaki}
          required
          gridColumn="span 3"
          {...register(1)}
        />
        <SLabel>得意先</SLabel>
        <SNumberField
          label="得意先コード"
          name="tokuiCd"
          size={6}
          code
          onBlur={handleBlurTokuiNm}
          {...register(2)}
        />
        <SNumberField
          label="得意先支店コード"
          name="tokuishitenCd"
          size={4}
          code
          dialog={<B000150 />}
          handleSubmit={handleSubmitTokuiNm}
          onBlur={handleBlurTokuiNm}
          {...register(3)}
        />
        <STextField label="得意先名" name="tokuiNm" size={40} disabled />
        <SLabel>出荷指示日</SLabel>
        <SDateField label="出荷指示日" name="shukashijiYmd" {...register(4)} />
        <SLabel>指示回数</SLabel>
        <SNumberField
          label="指示回数"
          name="shijikaisu"
          size={4}
          {...register(5)}
        />
        <SLabel>バッチ番号</SLabel>
        <SNumberField
          label="バッチ番号"
          name="batchNo"
          size={4}
          code
          gridColumn="span 2"
          dialog={<B000223 />}
          handleSubmit={handleSubmitBatchNm}
          onBlur={handleBlurBatchNm}
          {...register(6)}
        />
        <STextField label="バッチ名" name="batchNm" size={40} disabled />
        <SNormalButton
          label={labels.buttons.search}
          gridRow={3}
          gridColumn={8}
          align="right"
          onClick={handleClickSearchButton}
          {...register(7)}
        />
      </SFrame>
      <SFrame gridTemplateAreas="">
        <STable
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          pageSize={pageSize}
          width={1260}
          height={440}
          onPageChange={handleResetSearch}
        />
        <SFileButton
          label={labels.buttons.excel}
          disabled={disabledExcelBtn}
          align="right"
          onClick={handleClickExcelButton}
        />
      </SFrame>
    </FormProvider>
  );
};
